/**
 * Language routing.
 *
 * Each locale is a real URL — `/`, `/zh-hant`, `/zh-hans`, `/ja` — so a visitor
 * can link to, bookmark and share the language they read in, and so crawlers see
 * four documents rather than one. CloudFront rewrites every unmatched path to
 * index.html, and this module reads the language back out of the path.
 */

import { DEFAULT_LANG, LANGS, LANG_PATH, PATH_LANG } from '../content.js';

const STORAGE_KEY = 'veni-lang';

/** The language a path names, or null when it names none. */
export function langFromPath(pathname = location.pathname) {
  const segment = pathname.split('/').filter(Boolean)[0];
  return (segment && PATH_LANG[segment.toLowerCase()]) || null;
}

/** The stored preference, if it is still a language we publish. */
export function storedLang() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return LANGS.includes(saved) ? saved : null;
  } catch {
    return null;
  }
}

export function rememberLang(lang) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* private browsing — the URL still carries the choice */
  }
}

/** First browser-preferred language we publish. `zh` without a script defaults to Traditional. */
export function browserLang() {
  for (const tag of navigator.languages || [navigator.language || '']) {
    const lower = tag.toLowerCase();
    if (lower.startsWith('ja')) return 'ja';
    if (lower.startsWith('zh')) {
      if (/hans|-cn|-sg|-my/.test(lower)) return 'zh-Hans';
      return 'zh-Hant';
    }
    if (lower.startsWith('en')) return 'en';
  }
  return null;
}

/**
 * The language to render on load: an explicit URL wins, then a remembered
 * choice, then the browser's own preference.
 */
export function initialLang() {
  return langFromPath() || storedLang() || browserLang() || DEFAULT_LANG;
}

export function pathForLang(lang) {
  return LANG_PATH[lang] || LANG_PATH[DEFAULT_LANG];
}

/** Push a language into the URL bar without reloading, preserving any hash. */
export function pushLang(lang) {
  const url = pathForLang(lang) + location.hash;
  if (url !== location.pathname + location.hash) history.pushState({ lang }, '', url);
}

export function onRouteChange(handler) {
  window.addEventListener('popstate', () => handler(langFromPath() || DEFAULT_LANG));
}
